import ArrowBack from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Collapse,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import { Selector } from 'components/Form/Selectors/Selector';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import React, { useEffect, useState } from 'react';
import { Cell, Pie, PieChart } from 'recharts';
import { useAppSelector } from 'store/hook';
import { type EolDates, type ServiceView } from 'types/survivalAnalysis.types';
import LoadingDots from 'views/FleetOverview/components/AssetCardsV2/LoadingDots';
import { getUniqueAssetsFromComponent } from 'views/SurvivalAnalysis/Helper/Function/component.helper.function';
import { useFetchDashboardAssetsData } from 'views/SurvivalAnalysis/Helper/Function/fetch.helper.function';
import SquaresDashboardHandler from 'handlers/squaresHandlers/squaresDashboard.handler';
import {
  calculateFinancialMetrics,
  formatToDollars,
  getNumericValue,
  sortOrders,
  type MetricDetails,
} from 'views/SurvivalAnalysis/Helper/Function/helper.function';
import { SurvivalAnalysisDetailsCard } from 'views/SurvivalAnalysis/Helper/Modal/SurvivalAnalysis.details.card';
import SurvivalAnalysisDetailsListView from 'views/SurvivalAnalysis/Helper/Modal/SurvivalAnalysis.details.listView';
import { GetIcons } from '../../Helper/Icons/GetIcons.function';
import componentsImage from '../../Helper/Icons/componenent.png';
import dozerImage from '../../Helper/Icons/dozer.png';
import graderImage from '../../Helper/Icons/grader.png';
import haulerImage from '../../Helper/Icons/haultruck.png';
import lightTruckImage from '../../Helper/Icons/lighttruck.png';
import loaderImage from '../../Helper/Icons/loader.png';
import assetImage from '../../Helper/Icons/vehicle.png';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Option {
  id: string;
  display: string;
}

export const OverviewModalAssetDetails = ({
  onShowDetails,
  displayItems,
  uniqueAssets,
  detailType,
  handleModalSuccess,
}: {
  onShowDetails: any;
  displayItems: ServiceView;
  uniqueAssets: ServiceView[];
  detailType: string;
  handleModalSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const squaresHandler = new SquaresDashboardHandler();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const {
    assets,
    cumulative: assetsPerSubComponent,
    eol,
  } = useAppSelector((state) => state.survivalAnaysisOverview);

  const { assets: reducerAssets } = useAppSelector(
    (state) => state.assetReducer
  );

  const eolDates = eol as EolDates[];

  const [searchTerm, setSearchTerm] = useState('');
  const [sortCriteria, setSortCriteria] = useState<string>('eol-low');
  const [filter, setFilter] = useState('');
  const [displayMode, setDisplayMode] = useState('grid');
  const [selectedAsset, setSelectedAsset] = useState('');
  const [selectedComponent, setSelectedComponent] = useState('');
  const [selectedSubComponent, setSelectedSubComponent] = useState('');
  const [costPeriod, setCostPeriod] = useState('1');
  const [componentsOptions, setComponentsOptions] = useState<Option[]>([]);
  const [assetOptions, setAssetOptions] = useState<Option[]>([]);
  const [subComponentOptions, setSubComponentOptions] = useState<Option[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<any[]>([]);
  const [financialMetricsValues, setFinancialMetricsValues] =
    useState<MetricDetails>();
  const [financialMetricsValuesLoading, setFinancialMetricsValuesLoading] =
    useState(false);
  const [upComingService, setUpComingService] = useState('');
  const [open, setOpen] = React.useState(true);
  const { healthData } = useAppSelector(
    (state) => state.squaresPartsRecommendationsReducer
  );

  const [pieData, setPieData] = useState<any>([
    {
      name: 'Spend',
      value: 0,
    },
    {
      name: 'Remaining',
      value: 100,
    },
  ]);

  const pieColors = ['#0088FE', '#B2BEB5'];

  const fetchSquaresData = useFetchDashboardAssetsData();

  useEffect(() => {
    if (detailType === 'Assets') {
      setSelectedAsset(displayItems.item_id);
      setAssetOptions(
        uniqueAssets.map((asset) => ({
          id: asset.item_id,
          display: asset.item_id, // Adjust display format as necessary
        }))
      );
    } else {
      setSelectedComponent(displayItems.components_category);
      setSelectedSubComponent(displayItems.sub_component);

      const updatedAssetsItems = getUniqueAssetsFromComponent(
        uniqueAssets,
        displayItems.components_category,
        displayItems.sub_component
      );

      const uniqueAssetIds = new Set();
      const uniqueAssetsArray: React.SetStateAction<Option[]> = [];

      updatedAssetsItems.forEach((asset) => {
        if (!uniqueAssetIds.has(asset.asset_id)) {
          uniqueAssetIds.add(asset.asset_id);
          uniqueAssetsArray.push({
            id: asset.asset_id,
            display: asset.asset_id, // Adjust display format as necessary
          });
        }
        setAssetOptions([
          { id: 'All', display: 'All Assets' },
          ...uniqueAssetsArray,
        ]);
        setSelectedAsset('All');
      });
    }
  }, [detailType, displayItems, uniqueAssets]);

  useEffect(() => {
    const fetchData = async () => {
      setFinancialMetricsValuesLoading(true);
      const today = new Date();
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(today.getFullYear() - parseInt(costPeriod));
      const isAllselectedAsset = selectedAsset === 'All' ? '' : selectedAsset;
      try {
        await squaresHandler.getPartsRecommendations(new Date(), new Date());
        const financialMetricData = await fetchSquaresData(oneYearAgo, today);

        const financialMetric = calculateFinancialMetrics(
          financialMetricData,
          isAllselectedAsset
        );
        setFinancialMetricsValues(financialMetric);

        const numericPercentageOfTotal = getNumericValue(
          financialMetric?.percentageOfTotal
        );

        setPieData([
          {
            name: 'Spend',
            value: numericPercentageOfTotal,
          },
          {
            name: 'Remaining',
            value: 100 - numericPercentageOfTotal,
          },
        ]);
        setFinancialMetricsValuesLoading(false);
      } catch (error) {
        console.error('Error fetching or processing data:', error);
        setFinancialMetricsValuesLoading(false);
      }
    };

    if (selectedAsset && costPeriod) {
      void fetchData();
    }
  }, [selectedAsset, costPeriod]);

  useEffect(() => {
    const filterAndSortAssets = () => {
      const isAllselectedComponents =
        selectedComponent === 'All' ? '' : selectedComponent;
      const isAllselectedSubComponents =
        selectedSubComponent === 'All' ? '' : selectedSubComponent;
      const isAllselectedAsset = selectedAsset === 'All' ? '' : selectedAsset;

      if (detailType === 'Assets') {
        let updatedAssets = assets.filter((asset) =>
          asset.asset_id.includes(isAllselectedAsset)
        );
        updatedAssets = updatedAssets.filter((asset) =>
          asset.components_category
            ?.toLowerCase()
            .includes((isAllselectedComponents || searchTerm)?.toLowerCase())
        );

        updatedAssets = updatedAssets.filter((asset) =>
          asset.sub_component?.includes(isAllselectedSubComponents)
        );
        updatedAssets = updatedAssets.filter((asset) => {
          if (filter === 'high') {
            // Include both 'eol' and 'high' when the filter is set to 'high'
            return (
              asset.work_order_age_category?.includes('eol') ||
              asset.work_order_age_category?.includes('high')
            );
          } else {
            // Include only the specified filter for other values
            return asset.work_order_age_category?.includes(filter);
          }
        });
        updatedAssets = sortOrders(updatedAssets, sortCriteria);
        setFilteredAssets(updatedAssets);
      } else {
        const updatedAssetsItems = getUniqueAssetsFromComponent(
          uniqueAssets,
          isAllselectedComponents,
          isAllselectedSubComponents
        );
        let filteredComponents = Array.from(updatedAssetsItems).filter(
          (component) =>
            component.sub_component?.includes(isAllselectedSubComponents)
        );

        filteredComponents = Array.from(filteredComponents).filter(
          (component) =>
            component.asset_id?.toLowerCase().includes(searchTerm) ||
            component.components_category?.toLowerCase().includes(searchTerm)
        );
        filteredComponents = Array.from(filteredComponents).filter(
          (component) => {
            if (filter === 'high') {
              // Include both 'eol' and 'high' when the filter is set to 'high'
              return (
                component.work_order_age_category?.includes('eol') ||
                component.work_order_age_category?.includes('high')
              );
            } else {
              // Include only the specified filter for other values
              return component.work_order_age_category?.includes(filter);
            }
          }
        );

        filteredComponents = Array.from(filteredComponents).filter(
          (component) => component.asset_id?.includes(isAllselectedAsset)
        );
        filteredComponents = sortOrders(filteredComponents, sortCriteria);

        setFilteredAssets(Array.from(filteredComponents));
      }
    };

    filterAndSortAssets();
  }, [
    assets,
    selectedAsset,
    selectedComponent,
    selectedSubComponent,
    sortCriteria,
    searchTerm,
    filter,
    detailType,
    uniqueAssets,
  ]);

  useEffect(() => {
    const initializeOptions = () => {
      setComponentsOptions([
        { id: 'All', display: 'All Components' }, // Blank option
        ...Array.from(displayItems.componentsCategories).map((category) => ({
          id: category,
          display: category,
        })),
      ]);

      setSubComponentOptions([
        { id: 'All', display: 'All Sub-Components' }, // Blank option
        ...Array.from(displayItems.subComponentCategories).map((category) => ({
          id: category,
          display: category,
        })),
      ]);
      if (detailType === 'Assets') {
        setSelectedComponent('All');
        setSelectedSubComponent('All');
      } else {
        setSelectedSubComponent('All');
      }
    };

    initializeOptions();
  }, [detailType, displayItems, assetsPerSubComponent]);

  useEffect(() => {
    if (detailType === 'Assets' && selectedAsset && selectedAsset !== 'All') {
      const assetSpecificData = assets.filter((asset) =>
        asset.asset_id.includes(selectedAsset)
      );

      const uniqueComponents = new Set(
        assetSpecificData
          .map((asset) => asset.components_category)
          .filter(Boolean)
      );

      const uniqueSubComponents = new Set(
        assetSpecificData.map((asset) => asset.sub_component).filter(Boolean)
      );

      setComponentsOptions([
        { id: 'All', display: 'All Components' },
        ...Array.from(uniqueComponents).map((category) => ({
          id: category,
          display: category,
        })),
      ]);

      setSubComponentOptions([
        { id: 'All', display: 'All Sub-Components' },
        ...Array.from(uniqueSubComponents).map((category) => ({
          id: category,
          display: category,
        })),
      ]);

      if (
        selectedComponent !== 'All' &&
        !uniqueComponents.has(selectedComponent)
      ) {
        setSelectedComponent('All');
      }
      if (
        selectedSubComponent !== 'All' &&
        !uniqueSubComponents.has(selectedSubComponent)
      ) {
        setSelectedSubComponent('All');
      }
    }
  }, [selectedAsset, detailType, assets]);

  const handleChange = (event: {
    target: { value: { toString: () => React.SetStateAction<string> } };
  }) => {
    setSortCriteria(event.target.value.toString());
  };

  const handleFilterChange = (filter: React.SetStateAction<string>) => {
    setFilter(filter);
  };

  const handleDisplayModeChange = (mode: React.SetStateAction<string>) => {
    setDisplayMode(mode);
  };

  const handleAssetChange = (event: SelectChangeEvent<number>) => {
    setSelectedAsset(event.target.value.toString());
  };

  const handleComponentChange = (event: SelectChangeEvent<number>) => {
    setSelectedComponent(event.target.value.toString());
  };

  const handleSubComponentChange = (event: SelectChangeEvent<number>) => {
    setSelectedSubComponent(event.target.value.toString());
  };

  const handleCostPeriodChange = (event: SelectChangeEvent<number>) => {
    setCostPeriod(event.target.value as string);
  };

  useEffect(() => {
    if (eolDates && selectedAsset) {
      const filtered = eolDates.filter(
        (item) => item.asset_id === selectedAsset
      );

      if (filtered.length === 0) {
        console.log('No matching asset_id found');
        return;
      }

      const earliest = filtered.reduce((earliestItem, currentItem) => {
        return new Date(currentItem.eol) < new Date(earliestItem.eol)
          ? currentItem
          : earliestItem;
      });

      const earliestDate = new Date(earliest.eol);
      setUpComingService(earliestDate.toLocaleDateString());
    }
  }, [eolDates]);

  function getSelectedAssetMakeModel(arg: string) {
    const details = reducerAssets.find((item) => item.bumperNumber === arg);
    if (details) {
      return `${details.make} - ${details.model}`;
    }
    return 'All';
  }

  function getAssetImage(arg: string) {
    const details = reducerAssets.find((item) => item.bumperNumber === arg);
    if (details) {
      if (details.assetType === 'light_truck') {
        return lightTruckImage;
      }
      if (details.assetType === 'haul_truck') {
        return haulerImage;
      }
      if (details.assetType === 'loader') {
        return loaderImage;
      }
      if (details.assetType === 'dozer') {
        return dozerImage;
      }
      if (details.assetType === 'grader') {
        return graderImage;
      }
    }
    return assetImage;
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          mb: 4,
          display: 'flex',
          alignItems: 'left',
          justifyContent: 'left',
        }}
      >
        <IconButton
          sx={{ padding: 0, marginRight: 1 }}
          onClick={() => onShowDetails(null)}
        >
          <ArrowBack
            style={{
              color: enableDarkTheme
                ? ThemePalette.typography.white
                : ThemePalette.typography.grey,
            }}
          />
        </IconButton>
        <Typography
          fontStyle={{
            color: palette.textPrimary,
          }}
          variant="h6"
        >
          {detailType} {t('grade_importer_section_details')}
        </Typography>
      </Box>
      <Box sx={{ width: '100%', mb: 6 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography
              fontStyle={{
                color: palette.textPrimary,
              }}
            >
              {t('asset_ids')}
            </Typography>
            <Selector
              enableDarkTheme={enableDarkTheme}
              value={selectedAsset}
              selectorOptions={assetOptions}
              onChange={handleAssetChange}
              formControlSx={{
                minWidth: '100%',
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              fontStyle={{
                color: palette.textPrimary,
              }}
            >
              {t('component')}
            </Typography>
            <Selector
              enableDarkTheme={enableDarkTheme}
              value={selectedComponent}
              selectorOptions={componentsOptions}
              onChange={handleComponentChange}
              formControlSx={{
                minWidth: '100%',
              }}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <Typography
              fontStyle={{
                color: palette.textPrimary,
              }}
            >
              {t('sub_component_4')}
            </Typography>
            <Selector
              enableDarkTheme={enableDarkTheme}
              value={selectedSubComponent}
              selectorOptions={subComponentOptions}
              onChange={handleSubComponentChange}
              formControlSx={{
                minWidth: '100%',
              }}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 2 }}
        >
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Card
                className="card"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  backgroundColor: enableDarkTheme
                    ? palette.modalBackground
                    : palette.boxBackground,
                  color: palette.textPrimary,
                }}
              >
                <CardContent>
                  <Box sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'left',
                        mb: 4,
                      }}
                    >
                      <Typography variant="h6">
                        {t('selection_details')}
                      </Typography>
                      {upComingService && (
                        <Typography sx={{ ml: 1 }} variant="body2">
                          {t('upcoming_service')} <b>{upComingService}</b>
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'left' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'left',
                      }}
                    >
                      <GetIcons
                        icon={
                          detailType === 'Assets'
                            ? getAssetImage(selectedAsset)
                            : componentsImage
                        }
                        enableDarkTheme={enableDarkTheme}
                      />
                      <Box sx={{ ml: 4, mr: 4 }}>
                        <Typography variant="body1">
                          <strong>{t('asset_id_3')}</strong>
                        </Typography>
                        <Typography variant="body1">
                          {selectedAsset || 'Not Selected'}
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ ml: 4, mr: 4 }}>
                        <Typography variant="body1">
                          <strong>{t('make_model_5')}</strong>
                        </Typography>
                        <Typography variant="body1">
                          {getSelectedAssetMakeModel(selectedAsset)}
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ ml: 4, mr: 4 }}>
                        <Typography variant="body1">
                          <strong>{t('component')}</strong>
                        </Typography>
                        <Typography variant="body1">
                          {selectedComponent || 'Not Selected'}
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ ml: 4, mr: 4 }}>
                        <Typography variant="body1">
                          <strong>{t('sub_component_5')}</strong>
                        </Typography>
                        <Typography variant="body1">
                          {selectedSubComponent || 'Not Selected'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                className="card"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  backgroundColor: enableDarkTheme
                    ? palette.modalBackground
                    : palette.boxBackground,
                  color: palette.textPrimary,
                }}
              >
                <CardContent>
                  {!financialMetricsValuesLoading && selectedAsset !== 'All' ? (
                    <Box
                      sx={{
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ mr: 2 }}>
                        <Typography>Cost</Typography>
                        <Selector
                          enableDarkTheme={enableDarkTheme}
                          value={costPeriod}
                          selectorOptions={[
                            { id: '1', display: '1 Year' },
                            { id: '2', display: '2 Year' },
                            { id: '3', display: '3 Year' },
                            { id: '4', display: '4 Year' },
                          ]}
                          onChange={handleCostPeriodChange}
                          formControlSx={{
                            minWidth: '100%',
                          }}
                        />
                      </Box>
                      <Box sx={{ mr: 2 }}>
                        <Typography variant="body2">
                          {t('spend_on_this_asset')}
                        </Typography>
                        <Typography variant="h6">
                          {formatToDollars(financialMetricsValues?.total_cost)}
                        </Typography>
                        <Typography variant="body2" color="error">
                          {financialMetricsValues?.percentageFromAverage}{' '}
                          {t('more_than_average')}
                        </Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PieChart width={100} height={100}>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            outerRadius={50}
                            innerRadius={30}
                            dataKey="value"
                          >
                            {pieData.map((entry: any, index: number) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={pieColors[index]}
                                stroke="none"
                              />
                            ))}
                          </Pie>
                        </PieChart>
                        <Box sx={{ ml: 2 }}>
                          <Typography variant="body2">
                            {t('asset_vs_fleet')}
                          </Typography>
                          <Typography variant="h6" sx={{ ml: 1 }}>
                            {financialMetricsValues?.percentageOfTotal}
                          </Typography>
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {t('of_total_spend_of')}{' '}
                            {formatToDollars(financialMetricsValues?.total_sum)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%', // Ensures the Box takes the full height of the CardContent
                        textAlign: 'center', // Optional: Center text inside the Typography
                      }}
                    >
                      {financialMetricsValuesLoading &&
                      selectedAsset !== 'All' ? (
                        <Typography variant="body2">
                          {t('loading_asset_data')}
                          <LoadingDots enableDarkTheme={enableDarkTheme} />
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          {t('asset_is_not_selected')}
                        </Typography>
                      )}
                    </Box>
                  )}{' '}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Grid>
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Typography
          sx={{ paddingRight: '5px' }}
          fontStyle={{
            color: palette.textPrimary,
          }}
        >
          {t('all')} {detailType === 'Assets' ? 'Components' : 'Assets'}
        </Typography>

        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          {' '}
          <ButtonGroup variant="outlined">
            <IconButton
              onClick={() => {
                handleDisplayModeChange('grid');
              }}
            >
              <GridViewIcon
                style={{
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : ThemePalette.typography.grey,
                }}
              />
            </IconButton>
            <IconButton
              onClick={() => {
                handleDisplayModeChange('list');
              }}
            >
              <ListIcon
                style={{
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : ThemePalette.typography.grey,
                }}
              />
            </IconButton>
          </ButtonGroup>
        </Box>
      </Box>
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        mb={2}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder={`Search ${
            detailType === 'Components' ? 'asset' : 'component'
          }`}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          sx={{
            width: '240px',
            border: `0.5px solid ${
              enableDarkTheme
                ? ThemePalette.typography.grey
                : ThemePalette.typography.black
            }`,
            borderRadius: '4px',
          }} // You can set this to any width you prefer
          InputProps={{
            sx: {
              height: '35px',
              fontSize: '14px',
              minWidth: '131px',
              backgroundColor: enableDarkTheme
                ? palette.menuOptions
                : palette.boxBackground,
              color: palette.textPrimary,
            },
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon
                  style={{
                    color: enableDarkTheme
                      ? ThemePalette.typography.white
                      : ThemePalette.typography.grey,
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
        <ButtonGroup>
          <Button
            sx={{
              color: enableDarkTheme
                ? ThemePalette.typography.wheat
                : ThemePalette.typography.black,
              borderColor: ThemePalette.typography.grey,
              background:
                filter === '' ? ThemePalette.typography.grey : '00FFFFFF',
              fontSize: '14px',
              textTransform: 'none',
            }}
            onClick={() => {
              handleFilterChange('');
            }}
          >
            {t('all')}
          </Button>

          <Button
            sx={{
              color: enableDarkTheme
                ? ThemePalette.typography.wheat
                : ThemePalette.typography.black,
              borderColor: ThemePalette.typography.grey,
              background:
                filter === 'high' ? ThemePalette.typography.grey : '00FFFFFF',
              fontSize: '14px',
              textTransform: 'none',
            }}
            onClick={() => {
              handleFilterChange('high');
            }}
          >
            {t('high_risk')}
          </Button>
          <Button
            sx={{
              color: enableDarkTheme
                ? ThemePalette.typography.wheat
                : ThemePalette.typography.black,
              borderColor: ThemePalette.typography.grey,
              background:
                filter === 'med' ? ThemePalette.typography.grey : '00FFFFFF',
              textTransform: 'none',
            }}
            onClick={() => {
              handleFilterChange('med');
            }}
          >
            {t('medium_risk')}
          </Button>
          <Button
            sx={{
              color: enableDarkTheme
                ? ThemePalette.typography.wheat
                : ThemePalette.typography.black,
              borderColor: ThemePalette.typography.grey,
              background:
                filter === 'low' ? ThemePalette.typography.grey : '00FFFFFF',
              textTransform: 'none',
            }}
            onClick={() => {
              handleFilterChange('low');
            }}
          >
            {t('low_risk')}
          </Button>
        </ButtonGroup>

        <Box
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          justifyContent={'end'}
          gap={1}
        >
          <Typography
            sx={{ marginRight: '10px' }}
            fontSize="14px"
            fontStyle={{
              color: palette.textPrimary,
            }}
          >
            {t('sort_by_10')}
          </Typography>
          <Box width={'200px'}>
            <Selector
              enableDarkTheme={enableDarkTheme}
              value={sortCriteria}
              selectorOptions={[
                { id: 'low-eol', display: 'Low to High Risk' },
                { id: 'eol-low', display: 'High to Low Risk' },
              ]}
              height="20px"
              onChange={handleChange}
              minWidth="200px"
            />
          </Box>
        </Box>
      </Box>
      {displayMode === 'grid' ? (
        <>
          <Collapse in={open}>
            <Alert
              severity="info"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{ fontSize: '1rem', marginBottom: '1rem' }} // Set the text to a small size and add some space below
            >
              {t(
                'charts_shown_below_are_historical_distribution_of_components'
              )}
            </Alert>
          </Collapse>
          <SurvivalAnalysisDetailsCard
            components={filteredAssets}
            enableDarkTheme={enableDarkTheme}
            displayMode={displayMode}
            assetsPerSubComponent={assetsPerSubComponent}
            detailType={detailType}
            eolDates={eolDates}
            handleModalSuccess={handleModalSuccess}
            healthData={healthData}
          ></SurvivalAnalysisDetailsCard>
        </>
      ) : (
        <Card
          className="card"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: enableDarkTheme
              ? palette.modalBackground
              : palette.boxBackground,
            color: palette.textPrimary,
          }}
        >
          <SurvivalAnalysisDetailsListView
            components={filteredAssets}
            enableDarkTheme={enableDarkTheme}
            displayMode={displayMode}
            assetsPerSubComponent={assetsPerSubComponent}
            detailType={detailType}
          />
        </Card>
      )}
    </>
  );
};

export default OverviewModalAssetDetails;
